import { type CSSProperties, memo, type MouseEvent, type SyntheticEvent, type TouchEvent } from 'react'

import type { Highlight } from '@/api/services/highlight'
import { LoadingSpinner } from '@/components/shared/loading-spinner'

import { HIGHLIGHT_COLORS } from '../../highlight/highlight-colors'
import type { HighlightStartPoint } from '../../hooks/useImageHighlight'
import { CustomImage } from './custom-image'

type Props = {
  src: string | null
  pageIndex: number
  imageClassName: string
  placeholderStyle: CSSProperties | undefined
  highlights: Highlight[]
  highlightsVisible: boolean
  /** このページ上の開始点のみ渡す（他ページは null にして再描画を避ける） */
  startPoint: HighlightStartPoint | null
  onInteraction: (e: MouseEvent | TouchEvent, pageIndex: number) => void
  onHighlightClick: (highlightId: number) => void
  onImageLoad: (e: SyntheticEvent<HTMLImageElement>) => void
}

/** 1ページ分の表示。ページ読み込みのたびに全ページが再描画されないよう memo 化している */
export const ImagePage = memo(
  ({
    src,
    pageIndex,
    imageClassName,
    placeholderStyle,
    highlights,
    highlightsVisible,
    startPoint,
    onInteraction,
    onHighlightClick,
    onImageLoad,
  }: Props) => (
    <div
      className="relative cursor-pointer"
      onClick={(e) => onInteraction(e, pageIndex)}
      onTouchEnd={(e) => onInteraction(e, pageIndex)}
    >
      {src ? (
        <CustomImage
          src={src}
          alt={`Images ${pageIndex + 1}`}
          className={imageClassName}
          style={placeholderStyle}
          onLoad={onImageLoad}
        />
      ) : (
        <div
          className={`flex flex-col items-center justify-center ${placeholderStyle ? imageClassName : 'h-(--reader-vh)'}`}
          style={placeholderStyle}
        >
          <LoadingSpinner />
          <p className="mt-4 text-muted-foreground">画像 {pageIndex + 1} を読み込み中...</p>
        </div>
      )}

      {/* ハイライトオーバーレイ */}
      {highlightsVisible &&
        highlights
          .filter((h) => h.position_data.selections?.some((s) => s.page === pageIndex))
          .map((highlight) =>
            (highlight.position_data.selections ?? [])
              .filter((s) => s.page === pageIndex && s.rect)
              .map((selection, selIdx) => (
                <div
                  key={`${highlight.id}-${selIdx}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onHighlightClick(highlight.id)
                  }}
                  className={`absolute cursor-pointer hover:opacity-80 transition-opacity ${
                    HIGHLIGHT_COLORS.find((c) => c.value === highlight.color)?.className || 'bg-yellow-400/40'
                  }`}
                  style={{
                    left: `${selection.rect!.x}%`,
                    top: `${selection.rect!.y}%`,
                    width: `${selection.rect!.width}%`,
                    height: `${selection.rect!.height}%`,
                  }}
                />
              )),
          )}

      {/* 開始点マーカー */}
      {startPoint && (
        <div
          className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-yellow-400 border-2 border-white pointer-events-none animate-pulse"
          style={{ left: `${startPoint.x}%`, top: `${startPoint.y}%` }}
        />
      )}
    </div>
  ),
)

ImagePage.displayName = 'ImagePage'
